import { isEmpty } from 'lodash';
import { FilterType, ReportType, ShapeType } from '../../reportsTypes';
import { ReportCloneValues } from './createReportCloneFormSchema';
import {
  FilterCollection,
  PortCollection,
  ShapeCollection,
  deriveFilterFromValues,
  deriveShapeDictionaryFromValues,
} from './formValueBuilders';
import { ReportFilterType, ShapeDictionaryType } from '../../utils';
import { Ship } from './createDynamicFilterZodSchema';

export interface ReportFormModel {
  reportType: ReportType;
  reportTemplateName: string;
  reportName: string;
  dateRange: { from: Date; to: Date };
  miscellaneous: Record<string, unknown>;
  shapeDictionary: ShapeDictionaryType;
  filter: ReportFilterType | null;
  shapeType: ShapeType;
  filterType: FilterType;
  reportTemplateId?: string | null;
  ships?: Ship[] | null;
  shipAttribute?: 'colorBy' | 'filterBy';
}

export interface BuildReportFormModelOptions {
  reportType: ReportType;
  values: Partial<ReportCloneValues>;
  shapeType: ShapeType;
  filterType: FilterType;
  resources: {
    shapes?: ShapeCollection;
    filters?: FilterCollection;
    ports?: PortCollection;
  };
  existingShapeDictionary?: ShapeDictionaryType;
  fallbackShapeDictionary?: ShapeDictionaryType;
  fallbackReportTemplateName?: string;
  fallbackReportName?: string;
  additionalMiscellaneous?: Record<string, unknown>;
  reportTemplateId?: string | null;
  ships?: Ship[] | null;
  shipAttribute?: 'colorBy' | 'filterBy';
  instantShape?: boolean;
}

export function buildReportFormModel(
  options: BuildReportFormModelOptions
): ReportFormModel | null {
  const {
    reportType,
    values,
    shapeType,
    filterType,
    resources,
    existingShapeDictionary,
    fallbackShapeDictionary,
    fallbackReportTemplateName,
    fallbackReportName,
    additionalMiscellaneous,
    reportTemplateId,
    ships,
    shipAttribute,
    instantShape = false,
  } = options;

  const rawTemplateName = (values as Record<string, unknown>)[
    'report_template_name'
  ];
  const rawReportName = (values as Record<string, unknown>)['report_name'];
  const rawStartDatetime = values.start_datetime;
  const rawEndDatetime = values.end_datetime;

  // Convert to Date objects with proper type guards
  const startDatetime =
    rawStartDatetime instanceof Date
      ? rawStartDatetime
      : typeof rawStartDatetime === 'string' ||
        typeof rawStartDatetime === 'number'
      ? new Date(rawStartDatetime)
      : null;

  const endDatetime =
    rawEndDatetime instanceof Date
      ? rawEndDatetime
      : typeof rawEndDatetime === 'string' || typeof rawEndDatetime === 'number'
      ? new Date(rawEndDatetime)
      : null;

  if (!startDatetime || !endDatetime) {
    return null;
  }

  const baseMiscellaneous = (values.miscellaneous ?? {}) as Record<
    string,
    unknown
  >;
  const miscellaneous = {
    ...baseMiscellaneous,
    ...(additionalMiscellaneous ?? {}),
  } as Record<string, unknown>;

  // Always derive shapeDictionary from form values to ensure it's up-to-date
  // The existingShapeDictionary is only used as a fallback if derivation fails
  let shapeDictionary = deriveShapeDictionaryFromValues({
    values,
    reportType,
    shapeType,
    shapes: resources.shapes,
    ports: resources.ports,
    instantShape,
  });

  if (!shapeDictionary || isEmpty(shapeDictionary)) {
    shapeDictionary =
      existingShapeDictionary ?? fallbackShapeDictionary ?? null;
  }

  // Always derive filter from current form values to ensure real-time updates
  // (similar to how we handle shapeDictionary)
  const filter = deriveFilterFromValues({
    values,
    reportType,
    filterType,
    filters: resources.filters,
  });

  const reportTemplateName =
    typeof rawTemplateName === 'string' && rawTemplateName.trim() !== ''
      ? rawTemplateName
      : fallbackReportTemplateName ?? '';

  const reportName =
    typeof rawReportName === 'string' && rawReportName.trim() !== ''
      ? rawReportName
      : fallbackReportName ?? '';

  // Extract ships and shipAttribute from form values if available
  const extractedShips = (values as Record<string, unknown>)['ships'];
  const extractedShipAttribute = (values as Record<string, unknown>)[
    'shipAttribute'
  ];

  const finalShips =
    Array.isArray(extractedShips) && extractedShips.length > 0
      ? (extractedShips as Ship[])
      : ships ?? null;

  const finalShipAttribute =
    extractedShipAttribute === 'colorBy' ||
    extractedShipAttribute === 'filterBy'
      ? extractedShipAttribute
      : shipAttribute;

  return {
    reportType,
    reportTemplateName,
    reportName,
    dateRange: { from: startDatetime, to: endDatetime },
    miscellaneous,
    shapeDictionary: shapeDictionary ?? null,
    filter,
    shapeType,
    filterType,
    reportTemplateId: reportTemplateId ?? null,
    ships: finalShips,
    shipAttribute: finalShipAttribute,
  };
}

interface BuildReportFormModelFromContextOptions {
  reportType: ReportType;
  reportTemplateName?: string;
  reportName?: string;
  dateRange?: { from: Date; to: Date };
  miscellaneous?: Record<string, unknown>;
  shapeDictionary?: ShapeDictionaryType;
  filter?: ReportFilterType | null;
  shapeType: ShapeType;
  filterType?: FilterType;
  reportTemplateId?: string | null;
  ships?: Ship[] | null;
  shipAttribute?: 'colorBy' | 'filterBy';
}

export function buildReportFormModelFromContext(
  options: BuildReportFormModelFromContextOptions
): ReportFormModel | null {
  const {
    reportType,
    reportTemplateName,
    reportName,
    dateRange,
    miscellaneous,
    shapeDictionary,
    filter,
    shapeType,
    filterType,
    reportTemplateId,
    ships,
    shipAttribute,
  } = options;

  if (!dateRange?.from || !dateRange?.to) {
    return null;
  }

  return {
    reportType,
    reportTemplateName: reportTemplateName ?? '',
    reportName: reportName ?? '',
    dateRange,
    miscellaneous: miscellaneous ?? {},
    shapeDictionary: shapeDictionary ?? null,
    filter: filter ?? null,
    shapeType,
    filterType: filterType ?? 'normal',
    reportTemplateId: reportTemplateId ?? null,
    ships: ships ?? null,
    shipAttribute,
  };
}
